import React, { lazy, Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';
import { GlobalProvider } from 'context/GlobalProvider';
import PreLoader from 'components/common/loader/PreLoader';
import DefaultLayout from './DefaultLayout';
import RouteDefault from './RouteDefault';
import DefaultLayoutPrivateLoginRedirect from './DefaultLayoutPrivateLoginRedirect';
import { RoutesBase, NameRouteBases } from './constants-routes';
import RdStation from 'components/RdStation';
import RdStationCallback from 'components/RdStationCallback';

const PendentPayment = lazy(
  () => import('pages/my-account/my-travels/PendentPayment'),
);
const HotelHome = lazy(() => import('pages/travel-plan/hotel-plan/HotelHome'));
const FlightHome = lazy(() => import('pages/travel-plan/flight-plan/FlightHome'));
const CarsHome = lazy(() => import('pages/cars/CarsHome'));
const InsuranceHome = lazy(() => import('pages/insurance/InsuranceHome'));
const PackageHome = lazy(() => import('pages/package/PackageHome'));
const PackageAvailability = lazy(
  () => import('pages/package/package-availability/PackageAvailability'),
);
const CarDisponibilityOffer = lazy(
  () => import('pages/cars/car-disponibility-offer/CarDisponibilityOffer'),
);
const HospedagemAvailability = lazy(
  () => import('pages/package/package-availability/HospedagemAvailability'),
);
const CarsList = lazy(() => import('pages/cars/cars-list/CarsList'));
const RecommendsOndeViajar = lazy(
  () => import('pages/recommends/onde-viajar/RecommendsOndeViajar'),
);
const CarsDetails = lazy(() => import('pages/cars/cars-details/CarsDetails'));
const Faq = lazy(() => import('pages/faq/Faq'));
const ServiceTerm = lazy(() => import('pages/service-term/ServiceTerm'));
const PrivacyPolicy = lazy(() => import('pages/privacy-policy/PrivacyPolicy'));
const PageNotFound = lazy(() => import('pages/page-not-found/PageNotFound'));
const HotelList = lazy(
  () => import('pages/travel-plan/hotel-plan/hotel-list/HotelList'),
);
const FlightsList = lazy(
  () => import('pages/travel-plan/flight-plan/flights-list/FlightsList'),
);
const Hotel = lazy(() => import('pages/travel-plan/hotel-plan/hotel/Hotel'));
const MyInformation = lazy(() => import('pages/my-account/my-data/MyInformation'));
const SharedPayment = lazy(
  () => import('pages/shared-reservation/shared-payment/SharedPayment'),
);
const ShareRedirect = lazy(
  () => import('components/common/share-link/ShareRedirect'),
);
const DetailsMyTravel = lazy(
  () => import('pages/my-account/my-travels/DetailsMyTravel'),
);
const MyTravels = lazy(() => import('pages/my-account/my-travels/MyTravels'));
const Login = lazy(() => import('pages/auth/login/Login'));
const PasswordReset = lazy(() => import('pages/auth/password-reset/PasswordReset'));
const Recommends = lazy(() => import('pages/recommends/Recommends'));
const Checkout = lazy(() => import('pages/checkout/Checkout'));
const Home = lazy(() => import('pages/home/Home'));
const InsuranceList = lazy(
  () => import('pages/insurance/insurance-list/InsuranceList'),
);
const InsuranceDetail = lazy(
  () => import('pages/insurance/insurance-detail/InsuranceDetail'),
);
const FinalizedPurchase = lazy(
  () => import('pages/checkout/finalized-purchase/FinalizedPurchase'),
);

export default function Routes(): JSX.Element {
  return (
    <Suspense fallback={<PreLoader />}>
      <GlobalProvider>
        <Switch>
          <DefaultLayout exact
            path="/"
            component={Home}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.recommend.home}`}
            component={Recommends}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.recommend.whoTravel}`}
            component={RecommendsOndeViajar}
          />

          <DefaultLayout path={`/${RoutesBase.faq}/:type?`} component={Faq} />

          <DefaultLayout
            exact
            path={`/${RoutesBase.termsAndConditions}`}
            component={ServiceTerm}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.privacyPolicy}`}
            component={PrivacyPolicy}
          />

          <RouteDefault
            exact
            path={`/${RoutesBase.login}/:type?`}
            component={Login}
          />

          {/* Rotas de minha conta */}
          <DefaultLayoutPrivateLoginRedirect
            exact
            path={`/${RoutesBase.myAccount.home}`}
            component={MyInformation}
          />

          <DefaultLayoutPrivateLoginRedirect
            exact
            path={`/${RoutesBase.myAccount.informations}`}
            component={MyInformation}
          />

          <DefaultLayoutPrivateLoginRedirect
            exact
            path={`/${RoutesBase.myTravels.list}/:page?`}
            component={MyTravels}
          />

          <DefaultLayoutPrivateLoginRedirect
            exact
            path={`/${RoutesBase.myTravels.detail}/:id?/:token`}
            component={DetailsMyTravel}
          />

          <DefaultLayoutPrivateLoginRedirect
            exact
            path={`/${RoutesBase.myTravels.payment}/:id?/:token`}
            component={PendentPayment}
          />

          <RouteDefault
            exact
            path={`/${RoutesBase.changePassword}`}
            component={PasswordReset}
          />

          {/* Rotas de aereo */}
          <DefaultLayout
            exact
            path={`/${RoutesBase.flight.home}`}
            flow={NameRouteBases.flight}
            component={props => (
              <FlightHome {...props} flow={NameRouteBases.flight} />
            )}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.flight.list}`}
            flow={NameRouteBases.flight}
            component={props => (
              <FlightsList {...props} flow={NameRouteBases.flight} />
            )}
          />

          {/* Rotas de hotel */}
          <DefaultLayout
            exact
            path={`/${RoutesBase.hotel.home}`}
            flow={NameRouteBases.hotel}
            component={props => <HotelHome {...props} flow={NameRouteBases.hotel} />}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.hotel.list}/:idCidadeDestino/:dataStart?/:dataEnd?/:apartments?/:tokenIntegracao?/`}
            flow={NameRouteBases.hotel}
            component={props => <HotelList {...props} flow={NameRouteBases.hotel} />}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.hotel.detail}/:idCidadeDestino/:dataStart/:dataEnd/:apartaments/`}
            flow={NameRouteBases.hotel}
            component={props => <Hotel {...props} flow={NameRouteBases.hotel} />}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.hotel.complete.list}/:idCidadeDestino/:dataStart/:dataEnd/:apartments/:tokenIntegracao?`}
            flow={NameRouteBases.hotel}
            component={props => (
              <HospedagemAvailability {...props} flow={NameRouteBases.hotel} />
            )}
          />

          {/* Rotas de pacote */}
          <DefaultLayout
            exact
            path={`/${RoutesBase.package.home}`}
            flow={NameRouteBases.package}
            component={props => (
              <PackageHome {...props} flow={NameRouteBases.package} />
            )}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.package.hotel.list}/:idCidadeOrigem/:idCidadeDestino/:origin/:destiny/:dataStart/:dataEnd/:apartments/:tokenIntegracao?`}
            flow={NameRouteBases.package}
            component={props => (
              <HotelList {...props} flow={NameRouteBases.package} />
            )}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.package.hotel.list}/:idCidadeDestino/:destiny/:dataStart/:dataEnd/:apartments/:tokenIntegracao?`}
            flow={NameRouteBases.package}
            component={props => (
              <HotelList {...props} flow={NameRouteBases.package} />
            )}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.package.hotel.detail}/:idCidadeOrigem/:idCidadeDestino/:origin/:destiny/:dataStart/:dataEnd/:apartaments/`}
            flow={NameRouteBases.package}
            component={props => <Hotel {...props} flow={NameRouteBases.package} />}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.package.hotel.detail}/:idCidadeDestino/:destiny/:dataStart/:dataEnd/:apartaments/`}
            flow={NameRouteBases.package}
            component={props => <Hotel {...props} flow={NameRouteBases.package} />}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.packageCar.hotel.list}/:idCidadeOrigem/:idCidadeDestino/:origin/:destiny/:dataStart/:dataEnd/:apartments/:tokenIntegracao?`}
            flow={NameRouteBases.packageCar}
            component={props => (
              <HotelList {...props} flow={NameRouteBases.packageCar} />
            )}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.packageCar.hotel.detail}/:idCidadeOrigem/:idCidadeDestino/:origin/:destiny/:dataStart/:dataEnd/:apartaments/`}
            flow={NameRouteBases.packageCar}
            component={props => (
              <Hotel {...props} flow={NameRouteBases.packageCar} />
            )}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.package.flight.list}`}
            flow={NameRouteBases.package}
            component={props => (
              <FlightsList {...props} flow={NameRouteBases.package} />
            )}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.package.complete.list}/:idCidadeOrigem/:idCidadeDestino/:origin/:destiny/:dataStart/:dataEnd/:apartments/:tokenIntegracao?/`}
            flow={NameRouteBases.package}
            component={props => (
              <PackageAvailability
                {...props}
                flow={NameRouteBases.package}
                typePackage="hotel-aereo"
              />
            )}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.packageCar.complete.list}/:idCidadeOrigem/:idCidadeDestino/:origin/:destiny/:dataStart/:dataEnd/:apartments/:pickUpTime?/:dropOffTime?/:tokenIntegracao?`}
            flow={NameRouteBases.package}
            component={props => (
              <PackageAvailability
                {...props}
                flow={NameRouteBases.packageCar}
                typePackage="hotel-aereo-carro"
              />
            )}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.package.complete.hotelCarro}/:idCidadeDestino/:destiny/:dataStart/:dataEnd/:apartments/:tokenIntegracao?`}
            flow={NameRouteBases.package}
            component={props => (
              <PackageAvailability
                {...props}
                flow={NameRouteBases.package}
                typePackage="hotel-carro"
              />
            )}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.package.car.list}/:idPickUpLocation/:idDropOffLocation/:pickUpDateStart/:dropOffDateStart/:destino`}
            flow={NameRouteBases.packageCar}
            component={props => (
              <CarsList {...props} flow={NameRouteBases.packageCar} />
            )}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.package.car.detail}/:idPickUpLocation/:idDropOffLocation/:pickUpDateStart/:dropOffDateStart`}
            flow={NameRouteBases.packageCar}
            component={props => (
              <CarsDetails {...props} flow={NameRouteBases.packageCar} />
            )}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.packageCar.flight.list}`}
            flow={NameRouteBases.packageCar}
            component={props => (
              <FlightsList {...props} flow={NameRouteBases.packageCar} />
            )}
          />

          {/* Rotas de carros */}
          <DefaultLayout
            exact
            path={`/${RoutesBase.car.home}`}
            flow={NameRouteBases.car}
            component={props => <CarsHome {...props} flow={NameRouteBases.car} />}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.car.list}/:idPickUpLocation/:idDropOffLocation/:pickUpDateStart/:dropOffDateStart`}
            flow={NameRouteBases.car}
            component={props => <CarsList {...props} flow={NameRouteBases.car} />}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.car.detail}/:idPickUpLocation/:idDropOffLocation/:pickUpDateStart/:dropOffDateStart`}
            flow={NameRouteBases.car}
            component={props => <CarsDetails {...props} flow={NameRouteBases.car} />}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.car.complete.list}/:idPickUpLocation/:idDropOffLocation/:pickUpDateStart/:dropOffDateStart`}
            component={CarDisponibilityOffer}
          />

          {/* Rotas de Seguros */}
          <DefaultLayout
            exact
            path={`/${RoutesBase.insurance.home}`}
            flow={NameRouteBases.insurance}
            component={props => (
              <InsuranceHome {...props} flow={NameRouteBases.insurance} />
            )}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.insurance.list}/:destiny/:category/:startDate/:endDate`}
            flow={NameRouteBases.insurance}
            component={props => (
              <InsuranceList {...props} flow={NameRouteBases.insurance} />
            )}
          />

          <DefaultLayout
            exact
            path={`/${RoutesBase.insurance.detail}/:destiny/:category/:startDate/:endDate`}
            flow={NameRouteBases.insurance}
            component={props => (
              <InsuranceDetail {...props} flow={NameRouteBases.insurance} />
            )}
          />

          <DefaultLayout path={`/${RoutesBase.share}`} component={ShareRedirect} />

          <DefaultLayoutPrivateLoginRedirect
            exact
            path={`/${RoutesBase.checkout}`}
            component={Checkout}
          />

          <DefaultLayoutPrivateLoginRedirect
            exact
            path={`/${RoutesBase.checkoutFinalized}`}
            component={FinalizedPurchase}
          />

          {/* Rotas Reserva Compartilhada */}
          <DefaultLayout
            exact
            path={`/${RoutesBase.paymentShared}`}
            component={SharedPayment}
          />

          <Route
            exact
            path={`/rdstation/auth`}
            component={RdStation}
          />

          <Route
            exact
            path={`/rdstation/callback`}
            component={RdStationCallback}
          />

          <DefaultLayout component={PageNotFound} />
        </Switch>
      </GlobalProvider>
    </Suspense>
  );
}
